import { Box, Button, Card, CardContent, CardHeader, CircularProgress, SvgIcon, TableCell, TableRow, Typography } from '@mui/material';
import GenericTable, { GenericPaginator } from 'components/generic-table';
import { GenericHeader } from 'components/generic-table/GenericTableHeader';
import NoResultsTableRow from 'components/no-results-table-row';
import ContextLink, { RoutePathsKey } from 'components/_base/ContextLink';
import { useAuthenticatedUser, useContextRedirection } from 'hooks';
import { Patient } from 'models/patients.model';
import { Gender } from 'models/profile.model';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MaleIcon } from 'resources/icons/man.svg';
import { ReactComponent as FemaleIcon } from 'resources/icons/woman.svg';
import { canMakeAppointmentForPatient } from 'utils/func/Appointment.func';
import { convertDate } from 'utils/func/Date.func';
import { PrescriptionRequestMaker } from 'utils/func/Prescription.func';
import RoutePaths from 'utils/RoutePaths';

interface PatientListProps {
  patientDetailAction: { to: RoutePathsKey };
  patientList?: Patient[];
  paginatorData: GenericPaginator;
  isLoading?: boolean;
  handlePaginatorAction: (data: GenericPaginator) => void;
  showPrescriptionAction?: boolean;
  showPoctPrescriptionAction?: boolean;
  showAppointmentAction?: boolean;
  appointmentRouteKey?: 'DOCTOR_APPOINTMENT' | 'DOCTOR_APPOINTMENT_V2';
  isDataFromMPI?: boolean;
  requestCreatePatient?: (patient: any) => void;
}

const PatientList: FC<PatientListProps> = ({
  patientDetailAction,
  patientList,
  paginatorData,
  handlePaginatorAction,
  showPrescriptionAction = false,
  showAppointmentAction = false,
  appointmentRouteKey = 'DOCTOR_APPOINTMENT',
  showPoctPrescriptionAction = false,
  isDataFromMPI = false,
  requestCreatePatient,
  isLoading,
}) => {
  const { t } = useTranslation();

  const paginatorAction = useCallback(
    (data: GenericPaginator) => {
      handlePaginatorAction(data);
    },
    [handlePaginatorAction],
  );

  const tableHeaders: GenericHeader[] = [
    { id: 'fullname', label: 'patients.list.headers.fullname' },
    { id: 'gender', label: 'patients.list.headers.gender' },
    { id: 'birth_date', label: 'common.table.tableHead.birth_date' },
    { id: 'cns', label: 'patients.list.headers.cns' },
    { id: 'actions', label: undefined },
  ];

  const authenticatedUser = useAuthenticatedUser();
  const navigate = useContextRedirection();

  return (
    <>
      {patientList && (
        <Card>
          <CardHeader
            title={
              <Typography variant='h2' mb={4}>
                {t('patients.list.title', { count: paginatorData.total })}
              </Typography>
            }
          />
          <CardContent>
            {isLoading ? (
              <Box display='flex' justifyContent='center'>
                <CircularProgress />
              </Box>
            ) : (
              <GenericTable
                headers={tableHeaders}
                {...(patientList.length
                  ? {
                      pagination: paginatorData,
                      setPagination: paginatorAction,
                    }
                  : {
                      noPagination: true,
                    })}
              >
                {patientList.length ? (
                  patientList.map((patient, key) => {
                    return (
                      <TableRow hover key={key}>
                        <TableCell>
                          <Typography>{`${patient.first_name} ${(patient.marital_name || patient.last_name).toLocaleUpperCase()}`}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {patient.gender.toLowerCase() === Gender.FEMALE ? <SvgIcon fontSize='small' component={FemaleIcon} /> : <SvgIcon fontSize='small' component={MaleIcon} />}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{convertDate(patient.birth_date)}</Typography>
                        </TableCell>

                        <TableCell>
                          <Typography>{patient.cns}</Typography>
                        </TableCell>

                        <TableCell>
                          <Typography>
                            {authenticatedUser && isDataFromMPI && requestCreatePatient ? (
                              <Button type='button' variant='text' onClick={() => requestCreatePatient(patient)}>
                                {t('patients.list.details')}
                              </Button>
                            ) : (
                              <ContextLink {...patientDetailAction} params={patient.id}>
                                <Button type='button' variant='text'>
                                  {t('patients.list.details')}
                                </Button>
                              </ContextLink>
                            )}
                          </Typography>
                        </TableCell>
                        {showPrescriptionAction && !isDataFromMPI && (
                          <TableCell>
                            <Typography>
                              <Button
                                type='button'
                                variant='text'
                                onClick={() => {
                                  authenticatedUser && PrescriptionRequestMaker({ patientId: patient.id, navigation: navigate });
                                }}
                              >
                                {t('common.action.makePrescription')}
                              </Button>
                            </Typography>
                          </TableCell>
                        )}
                        {showPoctPrescriptionAction && (
                          <TableCell>
                            <Typography>
                              <Button
                                type='button'
                                variant='text'
                                onClick={() => {
                                  if (authenticatedUser && isDataFromMPI && requestCreatePatient) {
                                    requestCreatePatient(patient);
                                  } else {
                                    authenticatedUser && PrescriptionRequestMaker({ patientId: patient.id, navigation: navigate, poct: true });
                                  }
                                }}
                              >
                                {t('common.action.makePrescription')} POCT
                              </Button>
                            </Typography>
                          </TableCell>
                        )}
                        {showAppointmentAction && patient && (
                          <TableCell>
                            <Typography>
                              <Button type='button' variant='text' disabled={!canMakeAppointmentForPatient(patient)} onClick={() => navigate(RoutePaths[appointmentRouteKey]({ id: patient.id }))}>
                                {t('common.action.bookAppointment')}
                              </Button>
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <NoResultsTableRow colSpan={tableHeaders.length} />
                )}
              </GenericTable>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default PatientList;
